import { z } from 'zod';
import { IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
import { ReferenceDefinitionKeySchema } from './settings.js';
export const EquipmentStatusSchema = z.enum([
    'available',
    'deployed',
    'missing',
    'damaged',
    'out_of_service',
    'retired',
]);
/** Why an open deployment ended. */
export const EquipmentRemovalReasonSchema = z.enum([
    'removed',
    'transferred',
    'missing',
    'damaged',
    'out_of_service',
]);
/** Conditions a field user can report; each ends any open deployment. */
export const EquipmentConditionSchema = z.enum(['missing', 'damaged', 'out_of_service']);
export const MaintenanceTypeSchema = z.enum([
    'service',
    'repair',
    'calibration',
    'cleaning',
    'inspection',
    'other',
]);
export const MaintenanceStatusSchema = z.enum(['scheduled', 'completed', 'canceled']);
/** Tags and serial numbers compare on uppercase alphanumerics: "DH-0042" and "dh0042" are one asset. */
export function normalizeEquipmentIdentifier(value) {
    if (!value)
        return null;
    const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return normalized === '' ? null : normalized.slice(0, 100);
}
export const EquipmentAssetSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    assetTag: z.string(),
    categoryId: IdSchema,
    /** Label of the category definition, for display. */
    category: z.string(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    serialNumber: z.string().nullable(),
    status: EquipmentStatusSchema,
    /** Why the asset is missing, damaged, or out of service. */
    statusReason: z.string().nullable(),
    statusChangedAt: TimestampSchema.nullable(),
    homeLocation: z.string().nullable(),
    notes: z.string().nullable(),
    retiredAt: TimestampSchema.nullable(),
    /** The open deployment, when the asset is on a job. */
    currentDeployment: z
        .strictObject({
        id: IdSchema,
        jobFileId: IdSchema,
        propertyId: IdSchema.nullable(),
        areaId: IdSchema.nullable(),
        area: z.string().nullable(),
        placedByMembershipId: IdSchema,
        placedAt: TimestampSchema,
        expectedRemovalAt: TimestampSchema.nullable(),
    })
        .nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateEquipmentAssetRequestSchema = z.strictObject({
    assetTag: z.string().trim().min(1).max(100),
    categoryId: IdSchema,
    manufacturer: z.string().trim().max(120).nullable().default(null),
    model: z.string().trim().max(120).nullable().default(null),
    serialNumber: z.string().trim().max(120).nullable().default(null),
    homeLocation: z.string().trim().max(200).nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
});
export const UpdateEquipmentAssetRequestSchema = CreateEquipmentAssetRequestSchema.partial().extend({
    version: VersionSchema,
});
export const EquipmentListQuerySchema = ListQuerySchema.extend({
    status: EquipmentStatusSchema.optional(),
    categoryId: IdSchema.optional(),
    jobFileId: IdSchema.optional(),
    includeRetired: z.stringbool().default(false),
    /** Only deployments past their expected removal time. */
    overdue: z.stringbool().default(false),
});
export const EquipmentListSchema = z.strictObject({
    items: z.array(EquipmentAssetSchema),
    page: PageInfoSchema,
});
/** Scan or type a tag; the lookup never leaves the authenticated organization. */
export const EquipmentLookupQuerySchema = z.strictObject({
    tag: z.string().trim().min(1).max(100),
});
export const DeployEquipmentCommandSchema = z.strictObject({
    jobFileId: IdSchema,
    /** Defaults to the job file's property. */
    propertyId: IdSchema.nullable().default(null),
    /** An affected area of the job file; the free-text `area` stays for places without one. */
    areaId: IdSchema.nullable().default(null),
    area: z.string().trim().max(200).nullable().default(null),
    /** When the equipment is expected to come off the job; overdue lists use it. */
    expectedRemovalAt: TimestampSchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
    version: VersionSchema,
});
export const RemoveEquipmentCommandSchema = z.strictObject({
    notes: z.string().trim().max(1000).nullable().default(null),
    version: VersionSchema,
});
/** Moves deployed equipment to another job file in one transaction: the old deployment closes as transferred. */
export const TransferEquipmentCommandSchema = z.strictObject({
    jobFileId: IdSchema,
    propertyId: IdSchema.nullable().default(null),
    areaId: IdSchema.nullable().default(null),
    area: z.string().trim().max(200).nullable().default(null),
    expectedRemovalAt: TimestampSchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
    version: VersionSchema,
});
export const SetExpectedRemovalCommandSchema = z.strictObject({
    expectedRemovalAt: TimestampSchema.nullable(),
    version: VersionSchema,
});
/** Report missing, damaged, or out-of-service equipment; a deployed asset comes off its job. */
export const ReportEquipmentConditionCommandSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
    version: VersionSchema,
});
export const ReturnEquipmentToServiceCommandSchema = z.strictObject({
    notes: z.string().trim().max(500).nullable().default(null),
    version: VersionSchema,
});
export const EquipmentVersionCommandSchema = z.strictObject({ version: VersionSchema });
export const EquipmentDeploymentSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    assetId: IdSchema,
    jobFileId: IdSchema,
    propertyId: IdSchema.nullable(),
    areaId: IdSchema.nullable(),
    area: z.string().nullable(),
    placedByMembershipId: IdSchema,
    placedAt: TimestampSchema,
    expectedRemovalAt: TimestampSchema.nullable(),
    removedByMembershipId: IdSchema.nullable(),
    removedAt: TimestampSchema.nullable(),
    removalReason: EquipmentRemovalReasonSchema.nullable(),
    transferredToDeploymentId: IdSchema.nullable(),
    notes: z.string().nullable(),
});
/** Maintenance on an asset (specification 8.11): scheduled, completed with an outcome, or canceled. */
export const EquipmentMaintenanceEventSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    assetId: IdSchema,
    type: MaintenanceTypeSchema,
    status: MaintenanceStatusSchema,
    title: z.string(),
    notes: z.string().nullable(),
    dueAt: TimestampSchema.nullable(),
    scheduledByMembershipId: IdSchema,
    completedAt: TimestampSchema.nullable(),
    completedByMembershipId: IdSchema.nullable(),
    outcome: z.string().nullable(),
    canceledAt: TimestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const ScheduleMaintenanceRequestSchema = z.strictObject({
    type: MaintenanceTypeSchema,
    title: z.string().trim().min(1).max(200),
    notes: z.string().trim().max(2000).nullable().default(null),
    dueAt: TimestampSchema.nullable().default(null),
});
export const UpdateMaintenanceRequestSchema = z.strictObject({
    type: MaintenanceTypeSchema.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    dueAt: TimestampSchema.nullable().optional(),
    version: VersionSchema,
});
export const CompleteMaintenanceCommandSchema = z.strictObject({
    outcome: z.string().trim().max(2000).nullable().default(null),
    /** After a repair, put damaged or out-of-service equipment back in the available pool. */
    returnToService: z.boolean().default(false),
    version: VersionSchema,
});
export const CancelMaintenanceCommandSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
    version: VersionSchema,
});
export const MaintenanceListQuerySchema = ListQuerySchema.extend({
    assetId: IdSchema.optional(),
    status: MaintenanceStatusSchema.optional(),
    type: MaintenanceTypeSchema.optional(),
    /** Scheduled work due on or before this time (maintenance due lists). */
    dueBefore: TimestampSchema.optional(),
});
export const MaintenanceListSchema = z.strictObject({
    items: z.array(EquipmentMaintenanceEventSchema),
    page: PageInfoSchema,
});
export const EquipmentDeploymentListSchema = z.strictObject({
    items: z.array(EquipmentDeploymentSchema),
    page: PageInfoSchema,
});
/** Decimal values travel as strings so precision survives JSON. */
const DecimalStringSchema = z.string().regex(/^-?\d{1,9}(\.\d{1,3})?$/);
const ReadingValueSchema = z.number().min(-999_999_999).max(999_999_999);
/**
 * What may be measured on equipment of one category (specification 8.11): unit, allowed
 * range, and precision. Readings outside the range or finer than the precision are refused.
 */
export const EquipmentReadingDefinitionSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    categoryId: IdSchema,
    key: z.string(),
    label: z.string(),
    unit: z.string(),
    minValue: DecimalStringSchema.nullable(),
    maxValue: DecimalStringSchema.nullable(),
    decimals: z.number().int().min(0).max(3),
    sortOrder: z.number().int(),
    active: z.boolean(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateEquipmentReadingDefinitionRequestSchema = z
    .strictObject({
    key: ReferenceDefinitionKeySchema,
    label: z.string().trim().min(1).max(120),
    unit: z.string().trim().min(1).max(20),
    minValue: ReadingValueSchema.nullable().default(null),
    maxValue: ReadingValueSchema.nullable().default(null),
    decimals: z.number().int().min(0).max(3).default(1),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
})
    .refine((value) => value.minValue === null || value.maxValue === null || value.minValue <= value.maxValue, { path: ['maxValue'], message: 'Maximum must not be below the minimum' });
export const UpdateEquipmentReadingDefinitionRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(120).optional(),
    unit: z.string().trim().min(1).max(20).optional(),
    minValue: ReadingValueSchema.nullable().optional(),
    maxValue: ReadingValueSchema.nullable().optional(),
    decimals: z.number().int().min(0).max(3).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    version: VersionSchema,
});
export const EquipmentReadingDefinitionListQuerySchema = z.strictObject({
    includeInactive: z.stringbool().default(false),
});
export const EquipmentReadingDefinitionListSchema = z.strictObject({
    items: z.array(EquipmentReadingDefinitionSchema),
});
/**
 * A field reading on deployed equipment. Readings are never edited in place: a correction
 * records a replacement that supersedes the original, and both stay in the drying log.
 */
export const EquipmentReadingSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    deploymentId: IdSchema,
    assetId: IdSchema,
    jobFileId: IdSchema,
    areaId: IdSchema.nullable(),
    definitionId: IdSchema,
    definitionKey: z.string(),
    label: z.string(),
    value: DecimalStringSchema,
    unit: z.string(),
    takenAt: TimestampSchema,
    notes: z.string().nullable(),
    recordedByMembershipId: IdSchema,
    correctsReadingId: IdSchema.nullable(),
    correctionReason: z.string().nullable(),
    supersededAt: TimestampSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
});
export const RecordEquipmentReadingRequestSchema = z.strictObject({
    definitionId: IdSchema,
    value: ReadingValueSchema,
    /** Defaults to now; may not be in the future or before the equipment was placed. */
    takenAt: TimestampSchema.optional(),
    /** Defaults to the deployment's area. */
    areaId: IdSchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
});
export const CorrectEquipmentReadingRequestSchema = z.strictObject({
    value: ReadingValueSchema,
    takenAt: TimestampSchema.optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    reason: z.string().trim().min(1).max(500),
    version: VersionSchema,
});
export const EquipmentReadingListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    deploymentId: IdSchema.optional(),
    areaId: IdSchema.optional(),
    definitionId: IdSchema.optional(),
    from: TimestampSchema.optional(),
    to: TimestampSchema.optional(),
    includeSuperseded: z.stringbool().default(false),
});
export const EquipmentReadingListSchema = z.strictObject({
    items: z.array(EquipmentReadingSchema),
    page: PageInfoSchema,
});
